// Theme Constants for KaydSystems
// Centralized theme configuration for consistent styling across all pages

import React from 'react';
import { Link } from 'react-router-dom';

export const colors = {
  // Background gradients
  background: {
    main: 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950',
    card: 'bg-slate-900/50',
    cardHover: 'hover:bg-slate-900/70',
    code: 'bg-slate-800/50',
    table: 'bg-slate-800/50',
    tableHeader: 'bg-slate-800',
    tableRow: 'bg-slate-800/30',
  },

  // Text colors
  text: {
    primary: 'text-white',
    secondary: 'text-slate-300',
    tertiary: 'text-slate-400',
    muted: 'text-slate-500',
    gradient: 'bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent',
  },

  // Accent colors
  accent: {
    blue: 'text-blue-400',
    cyan: 'text-cyan-400',
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    purple: 'text-purple-400',
    orange: 'text-orange-400',
  },

  // Border colors
  border: {
    default: 'border-slate-800',
    hover: 'hover:border-blue-500/50',
    blue: 'border-blue-500/30',
    red: 'border-red-400',
    orange: 'border-orange-500',
    yellow: 'border-yellow-500',
    green: 'border-green-500/30',
  },

  // Alert/notification backgrounds
  alert: {
    red: 'bg-red-500/10',
    orange: 'bg-orange-500/10',
    yellow: 'bg-yellow-500/10',
    blue: 'bg-blue-500/10',
    green: 'bg-green-500/10',
  },

  // Button colors
  button: {
    primary: 'bg-gradient-to-r from-blue-500 to-cyan-400',
    primaryHover: 'hover:shadow-xl hover:shadow-blue-500/50',
    secondary: 'border border-slate-700 hover:bg-slate-800 hover:border-slate-600',
  },
};

export const spacing = {
  section: 'mb-8',
  container: 'py-12 px-4 sm:px-6 lg:px-8',
  card: 'p-8 md:p-12',
  small: 'p-4',
  list: 'space-y-2',
};

export const typography = {
  // Headings
  h1: 'text-4xl font-bold mb-4',
  h2: 'text-2xl font-semibold mb-4',
  h3: 'text-xl font-semibold mb-3',
  h4: 'text-lg font-semibold mb-2',
  
  // Body text
  body: 'text-slate-300 leading-relaxed',
  bodyMb: 'text-slate-300 mb-6 leading-relaxed',
  small: 'text-sm text-slate-500 italic mb-8',
  
  // Special text
  strong: 'text-white',
  code: 'text-sm text-cyan-400',
};

export const components = {
  // Card styles
  card: `bg-slate-900/50 border border-slate-800 rounded-2xl shadow-2xl`,
  cardHover: `bg-slate-900/50 border border-slate-800 rounded-xl hover:border-blue-500/50 hover:bg-slate-900/70 transition-all`,
  
  // Container
  pageContainer: 'min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 py-12 px-4 sm:px-6 lg:px-8 text-white',
  contentContainer: 'max-w-4xl mx-auto',
  
  // Lists
  list: 'list-disc list-inside space-y-2 text-slate-300 ml-4',
  orderedList: 'list-decimal list-inside space-y-2 text-slate-300 ml-4',
  
  // Alert boxes
  alertBox: (color = 'blue') => `${colors.alert[color]} border-l-4 ${colors.border[color]} p-4 rounded-r-lg`,
  
  // Contact box
  contactBox: 'bg-blue-500/10 border border-blue-500/30 rounded-lg p-6',
  
  // Table
  table: 'min-w-full bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden',
  tableCell: 'px-4 py-2 border border-slate-700 text-slate-300',
  tableHeader: 'px-4 py-2 border border-slate-700 text-white text-left',
  
  // Code block
  codeBlock: 'bg-slate-800/50 border border-slate-700 p-4 rounded-lg',
};

// Utility function to combine classes
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Pre-built component class combinations
export const presets = {
  pageWrapper: cn(components.pageContainer),
  mainCard: cn(components.card, components.contentContainer, spacing.card),
  sectionTitle: cn(typography.h2, colors.text.primary),
  subsectionTitle: cn(typography.h3, colors.accent.blue),
  bodyText: cn(typography.body),
  listItem: cn(components.list),
};

// Footer Component
export const Footer = () => {
  return (
    <footer className="border-t border-slate-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            {/* <h3 className="text-lg font-bold mb-4">KaydSystems</h3> */}
            <img 
                src="/img/logo.png" 
                alt="KaydSystems Logo" 
                className="h-16 w-auto hover:opacity-80 transition-opacity brightness-200"
            />
            <p className="text-slate-400 text-sm">Enterprise DevOps made simple for everyone.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
              <li><Link to="/admin/login" className="hover:text-white transition-colors">Get Started</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/TermsOfService" className="hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/ServiceLevelAgreement" className="hover:text-white transition-colors">SLA</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8 text-center text-slate-400 text-sm">
          <p>&copy; 2026 KaydSystems. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default {
  colors,
  spacing,
  typography,
  components,
  cn,
  presets,
  Footer,
};